import { backofficeDialogOptions } from 'src/app/layout/shared/dialogs/backoffice-dialog.options';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ResourceRS, ResourcesRS, ResourceSearchParams } from 'src/app/core/models/resources/resources-response';
import { SiteResponse } from 'src/app/core/models/sites-management/site-response';
import { ResourcesService } from 'src/app/core/services/api/resources.service';
import { MultimediaGalleryModalComponent } from '../multimedia-gallery-modal/multimedia-gallery-modal.component';

type MediaLibraryTab = 'this' | 'all';

/**
 * Rejilla reutilizable de la biblioteca de medios.
 *
 * Tiene dos modos de acotar por tienda, y son excluyentes:
 *
 * - siteFilter true (modo opcional) => un desplegable con las tiendas del usuario,
 *   junto a la busqueda y el tipo.
 * - siteFilter false (pantalla de tienda o selector de medios) => las pestanas
 *   "esta tienda / todas mis tiendas", con `siteId` como tienda de referencia.
 *   Sin siteId solo queda la vista de todas.
 *
 * Ademas:
 * - selectionMode true => un click en un item emite `selected` (modal selector).
 * - selectionMode false (por defecto) => un click abre el lightbox (MultimediaGalleryModalComponent).
 */
@Component({
  selector: 'app-media-library',
  templateUrl: './media-library.component.html',
  styleUrls: ['./media-library.component.scss']
})
export class MediaLibraryComponent implements OnInit, OnChanges, OnDestroy {

  @Input() siteId: string | null = null;
  @Input() selectionMode = false;
  @Input() pageMode = false;

  /**
   * Modo opcional: cambia las pestanas por un filtro de tienda.
   *
   * Es un input propio y no se deduce de `siteOptions` porque la lista de
   * tiendas llega por HTTP: deducirlo pintaria las pestanas durante el primer
   * segundo y el filtro despues, con el salto de layout correspondiente.
   */
  @Input() siteFilter = false;

  /** Tiendas del usuario. Alimentan el filtro y el nombre de tienda de cada tarjeta. */
  @Input() siteOptions: SiteResponse[] = [];

  /**
   * Fuerza un unico tipo y oculta el desplegable de filtro.
   *
   * Hace falta porque el `accept` solo limita la pestana "del equipo": sin esto, en un hueco
   * de solo imagen (icono de categoria, foto de variante, modo IA) se podia elegir un PDF
   * desde la biblioteca.
   */
  @Input() forcedType: ResourceRS.TypeEnum | null = null;
  @Output() selected = new EventEmitter<ResourcesRS>();

  /** Solo en modo seleccion: el usuario no tiene nada y pide subir. Lo atiende el selector. */
  @Output() uploadRequested = new EventEmitter<void>();

  readonly TypeEnum = ResourceRS.TypeEnum;

  activeTab: MediaLibraryTab = 'all';
  filterType: ResourceRS.TypeEnum | '' = '';
  searchValue = '';

  /** Tienda elegida en el filtro global. '' = todas mis tiendas (valor por defecto). */
  selectedSiteId = '';

  /** id -> nombre de tienda, para etiquetar las tarjetas sin recorrer el array en cada una. */
  private siteNames = new Map<string, string>();

  page = 0;
  readonly pageSize = 24;
  totalElements = 0;
  totalPages = 0;

  items: ResourcesRS[] = [];
  loading = true;

  /** Distingue "ha fallado la carga" de "no hay ficheros". Sin esto un 404 o un 500 se veian
   *  exactamente igual que una biblioteca vacia, y era imposible diagnosticar nada. */
  loadError = false;

  private readonly brokenItems = new Set<ResourcesRS>();
  private readonly searchSubject = new Subject<string>();
  private searchSubscription: Subscription;
  private loadVersion = 0;

  constructor(
    private resourcesService: ResourcesService,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    // El modo con filtro empieza por todas las tiendas salvo que reciba una
    // tienda concreta como contexto.
    this.selectedSiteId = this.siteFilter ? (this.siteId ?? '') : '';
    this.activeTab = this.hasSiteContext() ? 'this' : 'all';
    this.rebuildSiteNames();
    if (this.forcedType) {
      this.filterType = this.forcedType;
    }

    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      this.searchValue = value;
      this.page = 0;
      this.load();
    });

    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Las tiendas llegan por HTTP, despues del primer render: aqui solo se
    // refresca el diccionario de nombres. No se recarga la rejilla, que no
    // depende de esta lista.
    if (changes['siteOptions']) {
      this.rebuildSiteNames();
    }

    if (changes['siteId'] && !changes['siteId'].firstChange) {
      this.selectedSiteId = this.siteFilter ? (this.siteId ?? '') : '';
      this.activeTab = this.hasSiteContext() ? 'this' : 'all';
      this.page = 0;
      this.load();
    }
  }

  private rebuildSiteNames(): void {
    this.siteNames = new Map(
      (this.siteOptions || [])
        .filter(site => !!site?.id)
        .map(site => [site.id as string, site.site_name || ''])
    );
  }

  ngOnDestroy(): void {
    this.loadVersion++;
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  hasSiteContext(): boolean {
    return !!(this.siteId && this.siteId.trim() !== '');
  }

  onTabChange(tab: MediaLibraryTab): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.page = 0;
    this.load();
  }

  onSiteChange(siteId: string): void {
    this.selectedSiteId = siteId;
    this.page = 0;
    this.load();
  }

  /**
   * Nombre de la tienda de un fichero, solo cuando la vista mezcla varias.
   *
   * Con una tienda ya elegida en el filtro seria ruido: repetir el mismo nombre
   * en las 24 tarjetas no informa de nada.
   */
  siteNameFor(item: ResourcesRS): string | null {
    if (!this.siteFilter || this.selectedSiteId || !item.site_id) {
      return null;
    }
    return this.siteNames.get(item.site_id) || null;
  }

  onTypeChange(type: ResourceRS.TypeEnum | ''): void {
    this.filterType = type;
    this.page = 0;
    this.load();
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  clearSearch(): void {
    this.searchValue = '';
    this.onSearchInput('');
  }

  onPageChange(page: number): void {
    this.page = page - 1;
    this.load();
  }

  /**
   * Tienda que se manda al backend. Sin valor, el backend devuelve las de todas
   * las tiendas del usuario, que es la vista por defecto del modo con filtro.
   */
  private resolveSiteIdParam(): string | undefined {
    if (this.siteFilter) {
      return this.selectedSiteId || undefined;
    }
    return this.activeTab === 'this' && this.hasSiteContext() ? (this.siteId ?? undefined) : undefined;
  }

  load(): void {
    const version = ++this.loadVersion;
    this.loading = true;
    this.loadError = false;
    this.brokenItems.clear();

    const params: ResourceSearchParams = {
      siteId: this.resolveSiteIdParam(),
      type: this.forcedType ?? (this.filterType === '' ? undefined : this.filterType),
      q: this.searchValue || undefined,
      page: this.page,
      size: this.pageSize
    };

    this.resourcesService.searchResources(params).subscribe(
      (res) => {
        if (version !== this.loadVersion) return;
        this.items = res.content ?? [];
        this.totalElements = res.total_elements ?? 0;
        this.totalPages = res.total_page ?? 0;
        this.loading = false;
      },
      (err) => {
        if (version !== this.loadVersion) return;
        // NO se silencia: si no, un endpoint caido se ve igual que "no tienes ficheros".
        console.error("MEDIA-LIBRARY- fallo al cargar los recursos", err);
        this.loadError = true;
        this.items = [];
        this.totalElements = 0;
        this.totalPages = 0;
        this.loading = false;
      }
    );
  }

  /** Reintenta la carga tras un error. Lo llama el boton del estado de error. */
  retry(): void {
    this.load();
  }

  /**
   * Hay algun filtro puesto por el usuario.
   *
   * `forcedType` no cuenta: no lo pone el usuario, lo impone el hueco de destino
   * y no se puede quitar, asi que ofrecer "quitar filtros" por el seria mentir.
   */
  hasActiveFilters(): boolean {
    return !!this.searchValue
      || (!this.forcedType && this.filterType !== '')
      || (this.siteFilter && !!this.selectedSiteId);
  }

  /** Salida del estado vacio por filtros: devuelve la vista a "todo". */
  clearFilters(): void {
    this.searchValue = '';
    this.selectedSiteId = '';
    if (!this.forcedType) {
      this.filterType = '';
    }
    this.page = 0;
    this.load();
  }

  onItemClick(item: ResourcesRS, index: number): void {
    if (this.selectionMode) {
      this.selected.emit(item);
      return;
    }
    this.openLightbox(index);
  }

  openLightbox(startIndex: number): void {
    if (!this.items.length) {
      return;
    }
    const modalRef = this.modalService.open(MultimediaGalleryModalComponent, backofficeDialogOptions('gallery', 'workspace'));

    modalRef.componentInstance.mediaItems = this.items;
    modalRef.componentInstance.startIndex = startIndex;
  }

  isImage(item: ResourcesRS): boolean {
    return item.type === this.TypeEnum.PHOTO;
  }

  isBroken(item: ResourcesRS): boolean {
    return this.brokenItems.has(item);
  }

  onImageError(item: ResourcesRS): void {
    // El set marca el item como roto y Angular retira el <img> del DOM en el siguiente
    // ciclo de deteccion de cambios: no vuelve a intentarse la carga, asi que no hay bucle.
    this.brokenItems.add(item);
  }

  getTypeIcon(item: ResourcesRS): string {
    switch (item.type) {
      case this.TypeEnum.AUDIO: return 'bi-file-earmark-music';
      case this.TypeEnum.VIDEO: return 'bi-file-earmark-play';
      case this.TypeEnum.PDF: return 'bi-file-earmark-pdf';
      case this.TypeEnum.PHOTO: return 'bi-image-alt';
      default: return 'bi-file-earmark';
    }
  }

  getTypeLabelKey(item: ResourcesRS): string {
    switch (item.type) {
      case this.TypeEnum.PHOTO: return 'ml_file_photo';
      case this.TypeEnum.VIDEO: return 'ml_file_video';
      case this.TypeEnum.AUDIO: return 'ml_file_audio';
      case this.TypeEnum.PDF: return 'ml_file_pdf';
      default: return 'ml_file_generic';
    }
  }

  get firstItemNumber(): number { return this.totalElements ? this.page * this.pageSize + 1 : 0; }
  get lastItemNumber(): number { return Math.min((this.page + 1) * this.pageSize, this.totalElements); }
  trackByItem(index: number, item: ResourcesRS): string | number { return item.id || item.url || index; }

  formatBytes(bytes?: number): string | null {
    if (bytes === undefined || bytes === null) {
      return null;
    }
    if (bytes === 0) {
      return '0 KB';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    const rounded = value % 1 === 0 ? value.toString() : value.toFixed(1);
    return `${rounded} ${units[unitIndex]}`;
  }
}
